import React from "react";
import { useStore } from "zustand";
import { createStore } from "zustand/vanilla";

interface IAlert {
  duration: number;
  isOpen: boolean;
  message: string;
  severity: "error" | "info" | "success" | "warning";
}

interface IBlockingModal {
  content: React.ReactNode;
  isOpen: boolean;
  title?: string;
}

interface IConfirmation {
  content: React.ReactNode;
  customOnConfirmText?: string;
  customOnRejectText?: string;
  isDanger: boolean;
  isOpen: boolean;
  onConfirm?: () => void;
  onExit?: () => void;
  onReject?: () => void;
  title?: string;
}

interface IModal {
  content: React.ReactNode;
  isOpen: boolean;
  title?: React.ReactNode | string;
}

interface INotification {
  content: React.ReactNode;
  isOpen: boolean;
  onExit?: () => void;
  redirect?: string;
  title?: string;
}

interface IPopupActions {
  closeAlert: () => void;
  closeBlockingModal: () => void;
  closeConfirmation: () => void;
  closeModal: () => void;
  closeNotification: () => void;
  openAlert: ({
    duration,
    message,
    severity,
  }: { duration?: IAlert["duration"] } & Pick<IAlert, "message" | "severity">) => void;
  openBlockingModal: ({ content, title }: Pick<IBlockingModal, "content" | "title">) => void;
  openConfirmation: ({
    content,
    onConfirm,
    onExit,
    onReject,
    title,
  }: Partial<Pick<IConfirmation, "customOnConfirmText" | "customOnRejectText" | "isDanger" | "onExit" | "onReject">> &
    Pick<IConfirmation, "content" | "onConfirm" | "title">) => void;
  openModal: ({ content, title }: Pick<IModal, "content" | "title">) => void;
  openNotification: ({
    content,
    onExit,
    title,
  }: { content: INotification["content"] } & Partial<Pick<INotification, "onExit" | "redirect" | "title">>) => void;
}

interface IPopupStore {
  actions: IPopupActions;
  alert: IAlert;
  blockingModal: IBlockingModal;
  confirmation: IConfirmation;
  modal: IModal;
  notification: INotification;
}

/**
 * Create the Zustand store for managing the popup state.
 */
const PopupStore = createStore<IPopupStore>()((set) => ({
  actions: {
    /**
     * Close the alert popup.
     */
    closeAlert: () => {
      set((state) => ({
        alert: {
          ...state.alert,
          duration: 0,
          isOpen: false,
          message: "",
          severity: "success",
        },
      }));
    },
    /**
     * Close the modal popup.
     */
    closeBlockingModal: () => {
      set((state) => ({
        blockingModal: {
          ...state.blockingModal,
          content: undefined,
          isOpen: false,
          title: undefined,
        },
      }));
    },
    /**
     * Close the confirmation popup.
     */
    closeConfirmation: () => {
      set((state) => ({
        confirmation: {
          ...state.confirmation,
          content: undefined,
          customOnConfirmText: undefined,
          customOnRejectText: undefined,
          isDanger: false,
          isOpen: false,
          onConfirm: undefined,
          onExit: undefined,
          onReject: undefined,
          title: undefined,
        },
      }));
    },
    /**
     * Close the modal popup.
     */
    closeModal: () => {
      set((state) => ({
        modal: {
          ...state.modal,
          content: undefined,
          isOpen: false,
          title: undefined,
        },
      }));
    },
    /**
     * Close the confirmation popup.
     */
    closeNotification: () => {
      set((state) => {
        if (state.notification.onExit) {
          state.notification.onExit();
        }

        return {
          notification: {
            ...state.notification,
            content: undefined,
            isOpen: false,
            onExit: undefined,
            redirect: "",
            title: "",
          },
        };
      });
    },
    /**
     * Opens a custom alert with the specified properties.
     *
     * @param duration - The duration (in milliseconds) for which the alert should be displayed.
     * @param message - The message to display in the alert.
     * @param severity - The severity level of the alert ('success', 'info', 'warning', or 'error').
     */
    openAlert: ({ duration = 4000, message, severity }) => {
      set((state) => ({
        alert: {
          ...state.alert,
          duration,
          isOpen: true,
          message,
          severity,
        },
      }));
    },
    /** Opens a blocking modal with the specified content and title that prevents user interaction with the rest of the application.
     *
     * @param content - The content to display in the blocking modal.
     * @param title - The title of the blocking modal.
     * */
    openBlockingModal: ({ content, title }) => {
      set((state) => ({
        blockingModal: {
          ...state.blockingModal,
          content,
          isOpen: true,
          title,
        },
      }));
    },
    /**
     * Open a confirmation popup.
     *
     * @param title - The title of the confirmation popup.
     * @param content - The content of the confirmation popup.
     * @param customOnConfirmText - The optional custom text for the confirmation button.
     * @param customOnRejectText - The optional custom text for the rejection button.
     * @param isDanger - Whether the confirmation is dangerous.
     * @param onConfirm - The function to execute on confirmation.
     * @param onReject - The optional function to execute on rejection.
     * @param onExit - The optional function to execute on exit.
     */
    openConfirmation: ({
      content,
      customOnConfirmText,
      customOnRejectText,
      isDanger = false,
      onConfirm,
      onExit,
      onReject,
      title,
    }) => {
      set((state) => ({
        confirmation: {
          ...state.confirmation,
          content,
          customOnConfirmText,
          customOnRejectText,
          isDanger,
          isOpen: true,
          onConfirm,
          onExit: onExit ?? undefined,
          onReject: onReject ?? undefined,
          title,
        },
      }));
    },
    /**
     * Open a modal popup.
     * @param title - The title of the modal popup.
     * @param content - The content of the modal popup.
     */
    openModal: ({ content, title }) => {
      set((state) => ({
        modal: {
          ...state.modal,
          content,
          isOpen: true,
          title,
        },
      }));
    },
    /**
     * Open a notification popup.
     * @param title - The title of the notification popup.
     * @param content - The content of the notification popup.
     * @param redirect - The optional redirection URL after the notification is closed.
     * @param onExit - The optional function to execute on exit.
     */
    openNotification: ({ content, onExit, title }) => {
      set((state) => ({
        notification: {
          ...state.notification,
          ...state.notification,
          content,
          isOpen: true,
          onExit: onExit ?? undefined,
          title: title ?? "",
        },
      }));
    },
  },
  alert: {
    duration: 0,
    isOpen: false,
    message: "",
    severity: "success" as IAlert["severity"],
  },
  blockingModal: {
    content: undefined,
    isOpen: false,
    title: undefined,
  },
  confirmation: {
    content: undefined,
    customOnConfirmText: undefined,
    customOnRejectText: undefined,
    isDanger: false,
    isOpen: false,
    onConfirm: undefined,
    onExit: undefined,
    onReject: undefined,
    title: undefined,
  },
  modal: {
    content: undefined,
    isOpen: false,
    title: undefined,
  },
  notification: {
    content: undefined,
    isOpen: false,
    onExit: undefined,
    redirect: undefined,
    title: undefined,
  },
}));

/**
 * A custom hook to access the popup state from the Zustand store.
 *
 * For reference: https://github.com/pmndrs/zustand/blob/main/docs/guides/typescript.md#bounded-usestore-hook-for-vanilla-stores
 */
function usePopupState(): IPopupStore;
function usePopupState<Type>(selector: (state: IPopupStore) => Type): Type;
function usePopupState<Type>(selector?: (state: IPopupStore) => Type) {
  return useStore(PopupStore, selector!);
}

/**
 * A custom hook to access the popup actions.
 */
function usePopupActions() {
  return usePopupState((state) => state.actions);
}

/**
 * The popup actions object directly obtained from the Zustand store.
 * Because hooks cannot be called outside a React component.
 * This allows you to mutate the store from normal javascript.
 */
const PopupActions = PopupStore.getState().actions;

export { PopupActions, usePopupActions, usePopupState };
